import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Stack,
  TextField,
} from "@mui/material";
import {
  OrganisationPermission,
  useAddOrganisationUserMutation,
} from "src/store/api/generatedApi";
import { isFetchBaseQueryError } from "helpers";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "src/components/common/inputs/multiSelect";
import { ORGANISATION_PERMISSION_VALUES } from "components/organisationUsers/organisationPermissions.ts";

const DEFAULT_PERMISSIONS: OrganisationPermission[] = [
  "mission_control_viewer",
];

export const AddOrganisationUser = ({
  groupName,
  isOpen,
  onClose,
  onError,
}: {
  groupName: string;
  isOpen: boolean;
  onClose: () => void;
  onError: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [addOrganisationUser, { isLoading }] = useAddOrganisationUserMutation();

  const handleAddOrganisationUser = async () => {
    await addOrganisationUser({
      groupName,
      addOrganisationUserRequest: { email, permissions },
    })
      .unwrap()
      .then(() => {
        setEmail("");
        setPermissions(DEFAULT_PERMISSIONS);
        onClose();
      })
      .catch((err: unknown) => {
        if (isFetchBaseQueryError(err)) {
          onError(
            `Could not add organisation user (status code: ${err.status})`
          );
        }
      });
  };

  const handleClose = () => {
    setEmail("");
    setPermissions(DEFAULT_PERMISSIONS);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{t("addOrganisationUser")}</DialogTitle>
      <DialogContent sx={{ minWidth: "500px" }}>
        <Stack sx={{ m: 1, display: "flex", gap: 1 }}>
          <InputLabel id="permission-select-label">
            {t("permission")}
          </InputLabel>
          <MultiSelect<OrganisationPermission, OrganisationPermission>
            values={permissions}
            options={Array.from(ORGANISATION_PERMISSION_VALUES)}
            label={t("permission")}
            getOptionLabel={(permission) => permission}
            getOptionId={(permission) => permission}
            setValues={setPermissions}
            sx={{ minWidth: "200px" }}
          />

          <InputLabel>{t("email")}</InputLabel>
          <TextField
            label={t("email")}
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            sx={{ m: 1 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={() => void handleAddOrganisationUser()}
          disabled={email.trim() === ""}
          sx={{ textTransform: "none", borderRadius: 0 }}
        >
          {t("addOrganisationUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
